import type { Attachment, Task } from "@engram/core";
import { newId } from "@engram/core/id";
import type { Editor } from "@tiptap/core";
import { db } from "../../db/client";
import type { LocalMutation } from "../../db/protocol";

/*
 * Tasks and attachments are rows; the note body only holds placeholders that point at them
 * (`data-task-id`, `data-attachment`). Editing can break that link: undo or paste brings back a
 * placeholder whose row was deleted, copy/paste duplicates one, or it is pasted into another note.
 * Each placeholder must end up with its own live row in this note, so such placeholders get a
 * copy of the row under a new id.
 *
 * Rows whose placeholder is removed are deleted only when the editing session ends (note closed,
 * page hidden), so undo and cut/paste inside the note never lose them.
 */

type Kind = "task" | "att";
const ATTR: Record<Kind, string> = { task: "data-task-id", att: "data-attachment" };
const RE: Record<Kind, RegExp> = { task: /data-task-id="([^"]+)"/g, att: /data-attachment="([^"]+)"/g };

export interface Refs {
  task: string[];
  att: string[];
}

export function refsOf(html: string): Refs {
  return { task: [...html.matchAll(RE.task)].map((m) => m[1]!), att: [...html.matchAll(RE.att)].map((m) => m[1]!) };
}

export interface Remap {
  kind: Kind;
  old: string;
  nth: number; // which occurrence of `old`, in document order
  id: string;
}

const inList = (ids: string[]) => ids.map(() => "?").join(",");

/** Finds placeholders that need their own row; returns the fixed html and the rows to create. */
export async function reconcileRefs(noteId: string, html: string): Promise<{ html: string; muts: LocalMutation[]; remap: Remap[] }> {
  const refs = refsOf(html);
  if (!refs.task.length && !refs.att.length) return { html, muts: [], remap: [] };
  const uniq = (a: string[]) => [...new Set(a)];
  const [tasks, atts] = await Promise.all([
    refs.task.length ? db.query<Task & { deleted: number }>(`SELECT * FROM tasks WHERE id IN (${inList(uniq(refs.task))})`, uniq(refs.task)) : [],
    refs.att.length ? db.query<Attachment & { deleted: number }>(`SELECT * FROM attachments WHERE id IN (${inList(uniq(refs.att))})`, uniq(refs.att)) : [],
  ]);
  const now = Date.now();
  const muts: LocalMutation[] = [];
  const remap: Remap[] = [];
  const plan = <R extends { id: string; note_id: string; deleted: number }>(kind: Kind, ids: string[], rows: R[], copy: (r: R, id: string) => LocalMutation) => {
    const byId = new Map(rows.map((r) => [r.id, r]));
    const kept = new Set<string>();
    const nth = new Map<string, number>();
    for (const old of ids) {
      const n = nth.get(old) ?? 0;
      nth.set(old, n + 1);
      const row = byId.get(old);
      if (!row) continue; // unknown row (never synced here): leave it alone
      if (!row.deleted && row.note_id === noteId && !kept.has(old)) {
        kept.add(old);
        continue;
      }
      const id = newId();
      muts.push(copy(row, id));
      remap.push({ kind, old, nth: n, id });
    }
  };
  plan("task", refs.task, tasks, (t, id) => ({
    t: "tasks",
    id,
    set: { note_id: noteId, title: t.title, completed_at: t.completed_at, due_at: t.due_at, due_all_day: t.due_all_day, reminder_at: t.reminder_at, flagged: t.flagged, recurrence: t.recurrence, position: t.position, created: now, updated: now },
  }));
  plan("att", refs.att, atts, (a, id) => ({
    t: "attachments",
    id,
    set: { note_id: noteId, sha256: a.sha256, filename: a.filename, mime: a.mime, size: a.size, width: a.width, height: a.height, created: now },
  }));
  if (!remap.length) return { html, muts, remap };
  let out = html;
  for (const kind of ["task", "att"] as Kind[]) {
    const mine = remap.filter((r) => r.kind === kind);
    if (!mine.length) continue;
    const seen = new Map<string, number>();
    out = out.replace(RE[kind], (whole, old: string) => {
      const n = seen.get(old) ?? 0;
      seen.set(old, n + 1);
      const hit = mine.find((r) => r.old === old && r.nth === n);
      return hit ? `${ATTR[kind]}="${hit.id}"` : whole;
    });
  }
  return { html: out, muts, remap };
}

/** Points the live editor's placeholders at their new rows (same occurrence order as the html). */
export function applyRemap(editor: Editor, remap: Remap[]) {
  if (!remap.length || editor.isDestroyed) return;
  const { tr, doc } = editor.state;
  const seen = new Map<string, number>();
  doc.descendants((node, pos) => {
    const kind: Kind | null = node.type.name === "taskRef" ? "task" : node.attrs.attachment ? "att" : null;
    if (!kind) return;
    const attr = kind === "task" ? "id" : "attachment";
    const old = node.attrs[attr] as string;
    const key = `${kind}:${old}`;
    const n = seen.get(key) ?? 0;
    seen.set(key, n + 1);
    const hit = remap.find((r) => r.kind === kind && r.old === old && r.nth === n);
    if (hit) tr.setNodeMarkup(pos, undefined, { ...node.attrs, [attr]: hit.id });
  });
  if (tr.docChanged) editor.view.dispatch(tr);
}

/** Deletes this note's rows whose placeholders were removed during the session. */
export async function deleteRemoved(noteId: string, seen: Refs, html: string) {
  const now = refsOf(html);
  const gone = (kind: Kind) => [...new Set(seen[kind])].filter((id) => !now[kind].includes(id));
  const goneTasks = gone("task");
  const goneAtts = gone("att");
  if (!goneTasks.length && !goneAtts.length) return;
  const [tasks, atts] = await Promise.all([
    goneTasks.length ? db.query<{ id: string }>(`SELECT id FROM tasks WHERE deleted = 0 AND note_id = ? AND id IN (${inList(goneTasks)})`, [noteId, ...goneTasks]) : [],
    goneAtts.length ? db.query<{ id: string }>(`SELECT id FROM attachments WHERE deleted = 0 AND note_id = ? AND id IN (${inList(goneAtts)})`, [noteId, ...goneAtts]) : [],
  ]);
  const muts: LocalMutation[] = [
    ...tasks.map((r) => ({ t: "tasks" as const, id: r.id, del: true as const })),
    ...atts.map((r) => ({ t: "attachments" as const, id: r.id, del: true as const })),
  ];
  if (muts.length) await db.mutate(muts);
}
